import { useAvatarSessionCleanup } from '../hooks/useAvatarSessionCleanup';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AssignmentIndOutlined as AssignmentIndOutlinedIcon,
  Check as CheckIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ChatOutlined as ChatOutlinedIcon,
  Close as CloseIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  MicNone as MicNoneIcon,
  PersonOutline as PersonOutlineIcon,
} from "@mui/icons-material";
import { Box, Button, CircularProgress, GlobalStyles, IconButton, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AvatarSection } from "../components/Avatar";
import { ChatContainer } from "../components/Chat";
import { DiagnosisNotes } from "../components/DiagnosisComponents/DiagnosisNotes";
import { RubricModal } from "../components/DiagnosisComponents/RubricModal";
import { EndDiagnosticModal } from "../components/DiagnosisComponents/modals/EndDiagnosticModal";
import { SpeechRecognition } from "../components/Speech";
import { useAppDispatch, useAppSelector } from "../store";
import {
  selectCurrentSessionWithDetails,
  selectDiagnosticReadiness,
  selectframeworkCategoryStatusList,
  selectIsProcessing,
  selectMessages,
  selectScoringRubric,
  selectSpeechState,
  selectAvatarState,
} from "../store/selectors";
import {
  askQuestionWithSpeech,
  connectAvatar,
  disconnectAvatar,
  submitDiagnosis,
} from "../store/thunks/sessionThunks";
import { connectSpeech, disconnectSpeech } from "../store/slices/speechSlice";
import type { PatientDateOfBirth, PrescriptionEntry } from "../types";
import type { ExchangeTimingMetadata } from "../services/exchangeTiming";

type SessionPanel =
  | "patient"
  | "prescriptions"
  | "history"
  | "rubric"
  | "submit"
  | "conversation";

interface ShellOverlayButtonProps {
  icon: ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const ShellOverlayButton: React.FC<ShellOverlayButtonProps> = ({
  icon,
  label,
  isActive,
  onClick,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <Button
      className={`diagnosis-shell-button${isActive ? " diagnosis-shell-button--active" : ""}`}
      onClick={onClick}
      aria-label={label}
      sx={{
        minWidth: 0,
        width: { xs: 44, md: 58 },
        height: { xs: 44, md: 58 },
        "@media (min-width: 1001px) and (max-height: 850px)": {
          width: 58,
          height: 58,
        },
        borderRadius: "999px",
        border: "solid",
        borderWidth: isActive ? 3 : 1,
        borderColor: isActive
          ? "#3399A3"
          : isDarkMode
            ? "#334155"
            : "rgba(15, 23, 42, 0.08)",
        backgroundColor: isActive
          ? isDarkMode
            ? "rgba(51, 153, 163, 0.2)"
            : "rgba(51, 153, 163, 0.08)"
          : isDarkMode
            ? "#1f2937"
            : "rgba(15, 23, 42, 0.04)",
        color: isDarkMode ? "#f8fafc" : "#111827",
        boxShadow: isActive
          ? isDarkMode
            ? "0 0 0 1px rgba(51, 153, 163, 0.34)"
            : "0 0 0 1px rgba(51, 153, 163, 0.12)"
          : "none",
        transition: "transform 180ms ease, background-color 180ms ease, box-shadow 180ms ease",
        "&:hover": {
          transform: "translateY(-1px)",
          backgroundColor: isActive
            ? isDarkMode
              ? "rgba(51, 153, 163, 0.28)"
              : "rgba(51, 153, 163, 0.12)"
            : isDarkMode
              ? "#273449"
              : "rgba(15, 23, 42, 0.08)",
        },
      }}
    >
      {icon}
    </Button>
  );
};

const PrescriptionOverlayButtonIcon: React.FC = () => (
  <Box
    component="span"
    sx={{
      width: 16,
      height: 16,
      display: "block",
      backgroundColor: "currentColor",
      mask: "url('/prescription.svg') center / contain no-repeat",
      WebkitMask: "url('/prescription.svg') center / contain no-repeat",
    }}
  />
);

const MedicalHistoryIcon: React.FC = () => (
  <Box
    component="span"
    sx={{
      width: 20,
      height: 20,
      display: "block",
      backgroundColor: "currentColor",
      mask: "url('/medical-history.svg') center / contain no-repeat",
      WebkitMask: "url('/medical-history.svg') center / contain no-repeat",
    }}
  />
);

interface OverlayPanelProps {
  title: string;
  icon: ReactNode;
  side: "left" | "right";
  onClose: () => void;
  children: ReactNode;
  variant?: "default" | "conversation" | "rubric" | "prescription";
}

const OverlayPanel: React.FC<OverlayPanelProps> = ({
  title,
  icon,
  side,
  onClose,
  children,
  variant = "default",
}) => (
  <Paper
    elevation={0}
    sx={{
      position: "absolute",
      top: -10,
      [side]: {
        xs: 0,
        md: -12,
      },
      width: { xs: "calc(100% - 32px)", md: side === "left" ? 374 : variant === "conversation" ? 358 : 358 },
      maxHeight: {
        xs: variant === "conversation" ? "none" : "calc(100% - 32px)",
        md: variant === "conversation"
          ? "none"
          : variant === "rubric"
            ? "min(624px, calc(100vh - 168px))"
            : "calc(100% - 48px)",
      },
      overflow: "hidden",
      borderRadius: variant === "rubric" ? "16px" : "22px",
      border: "1px solid rgba(15, 23, 42, 0.11)",
      backgroundColor: "rgba(255, 255, 255, 0.98)",
      backdropFilter: "blur(12px)",
      boxShadow: variant === "rubric" ? "0 18px 42px rgba(15, 23, 42, 0.12)" : "0 20px 48px rgba(15, 23, 42, 0.14)",
      zIndex: 10,
      animation: "overlay-pop 180ms ease-out",
      display: "flex",
      flexDirection: "column",
      "@keyframes overlay-pop": {
        from: {
          opacity: 0,
          transform: side === "left" ? "translateX(-10px)" : "translateX(10px)",
        },
        to: {
          opacity: 1,
          transform: "translateX(0)",
        },
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: variant === "conversation" ? 2.5 : variant === "rubric" ? 3.1 : variant === "prescription" ? 2.5 : 3,
        pt: variant === "conversation" ? 2.5 : variant === "rubric" ? 2.9 : variant === "prescription" ? 2.35 : 3,
        pb: variant === "conversation" ? 2.25 : variant === "rubric" ? 2.35 : variant === "prescription" ? 1.35 : 2,
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box sx={{ color: "#111827", display: "flex", alignItems: "center" }}>{icon}</Box>
        <Typography sx={{ fontSize: variant === "rubric" ? "0.95rem" : "1.05rem", fontWeight: variant === "rubric" ? 700 : 600, color: "#111827" }}>
          {title}
        </Typography>
      </Box>
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          width: 40,
          height: 40,
          backgroundColor: "rgba(15, 23, 42, 0.04)",
          color: "#111827",
          flexShrink: 0,
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
    <Box
      sx={{
        px: variant === "conversation" ? 2.5 : variant === "rubric" ? 3.1 : variant === "prescription" ? 2.5 : 3,
        pb: variant === "conversation" ? 2.5 : variant === "rubric" ? 3.2 : variant === "prescription" ? 2 : 3,
        overflowY: "auto",
        minHeight: 0,
        flex: 1,
        boxSizing: "border-box",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(15, 23, 42, 0.12)",
          borderRadius: "999px",
        },
      }}
    >
      {children}
    </Box>
  </Paper>
);

const formatElapsedTime = (startTime?: string) => {
  if (!startTime) {
    return "0:00";
  }

  const elapsedMs = Math.max(0, Date.now() - new Date(startTime).getTime());
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const DISPLAY_FALLBACK = "Not provided";

const displayValue = (value?: string | number | null) => {
  if (typeof value === "number") {
    return value.toString();
  }

  return value?.trim() || DISPLAY_FALLBACK;
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const parseInteger = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isInteger(value)) {
    return value;
  }

  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    return Number(value.trim());
  }

  return null;
};

const isValidDateOfBirth = (day: number, month: number, year: number) => {
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

const parseDateOfBirthParts = (value: unknown): PatientDateOfBirth => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const source = value as Partial<Record<keyof PatientDateOfBirth, unknown>>;
    return {
      day: parseInteger(source.day),
      month: parseInteger(source.month),
      year: parseInteger(source.year),
    };
  }

  const text = typeof value === "string" ? value.trim() : "";
  if (!text) {
    return { day: null, month: null, year: null };
  }

  const parsedDate = new Date(text);
  if (!Number.isNaN(parsedDate.getTime())) {
    return {
      day: parsedDate.getDate(),
      month: parsedDate.getMonth() + 1,
      year: parsedDate.getFullYear(),
    };
  }

  const numericMatch = text.match(/\b(\d{1,2})[/-](\d{1,2})[/-](\d{4})\b/);
  if (!numericMatch) {
    return { day: null, month: null, year: null };
  }

  const first = Number(numericMatch[1]);
  const second = Number(numericMatch[2]);
  return {
    day: first > 12 ? first : second,
    month: first > 12 ? second : first,
    year: Number(numericMatch[3]),
  };
};

const calculateAge = (day: number, month: number, year: number) => {
  const today = new Date();
  let age = today.getFullYear() - year;
  const hasHadBirthdayThisYear =
    today.getMonth() + 1 > month ||
    (today.getMonth() + 1 === month && today.getDate() >= day);

  if (!hasHadBirthdayThisYear) {
    age -= 1;
  }

  return age;
};

const formatDateOfBirthForOverlay = (dateOfBirth: unknown, fallbackAge?: number | null) => {
  const { day, month, year } = parseDateOfBirthParts(dateOfBirth);

  if (
    day !== null &&
    month !== null &&
    year !== null &&
    year >= 1000 &&
    year <= 9999 &&
    isValidDateOfBirth(day, month, year)
  ) {
    const age = calculateAge(day, month, year);
    return `${MONTH_NAMES[month - 1]} ${day}, ${year} (${age} years old)`;
  }

  return fallbackAge ? `Age ${fallbackAge}` : DISPLAY_FALLBACK;
};

const formatAddressForOverlay = (patientInformation: unknown) => {
  if (!patientInformation || typeof patientInformation !== "object" || Array.isArray(patientInformation)) {
    return DISPLAY_FALLBACK;
  }

  const source = patientInformation as Record<string, unknown>;
  const legacyAddress = typeof source.address === "string" ? source.address.trim() : "";
  const streetAddress = displayValue(source.street_address as string | null);
  const city = typeof source.city === "string" ? source.city.trim() : "";
  const provinceState = typeof source.province_state === "string" ? source.province_state.trim() : "";
  const postalZipCode = typeof source.postal_zip_code === "string" ? source.postal_zip_code.trim() : "";
  const cityProvinceLine = [city, provinceState].filter(Boolean).join(", ");
  const addressLines = [
    streetAddress === DISPLAY_FALLBACK ? "" : streetAddress,
    cityProvinceLine,
    postalZipCode,
  ].filter(Boolean);

  return addressLines.length > 0 ? addressLines.join("\n") : legacyAddress || DISPLAY_FALLBACK;
};

const getPrescriptionMedicationLabel = (entry: PrescriptionEntry) => {
  const doseSuffix = entry.dose && !entry.name.toLowerCase().includes(entry.dose.toLowerCase())
    ? ` ${entry.dose}`
    : "";

  return `${entry.name}${doseSuffix}`.trim();
};

const normalizePrescriptionForOverlay = (entry: Partial<PrescriptionEntry>): PrescriptionEntry => ({
  name: entry.name || "",
  category: entry.category || "prescription",
  dose: entry.dose || "",
  patient_name: entry.patient_name || "",
  written_date: entry.written_date || "",
  directions: entry.directions || "",
  quantity: entry.quantity || "",
  refills: entry.refills || "",
  prescriber: entry.prescriber || "",
  signature: entry.signature || "",
});

const normalizePrescriptionListForOverlay = (value: unknown): PrescriptionEntry[] => {
  if (Array.isArray(value)) {
    return value.map((entry) => normalizePrescriptionForOverlay(entry));
  }

  if (value && typeof value === "object") {
    const entry = normalizePrescriptionForOverlay(value as Partial<PrescriptionEntry>);
    return entry.name.trim() ? [entry] : [];
  }

  return [];
};

export const DiagnosisSession: React.FC = () => {
  const dispatch = useAppDispatch();
  const thunkDispatch = dispatch as any;
  const sessionDetails = useAppSelector(selectCurrentSessionWithDetails);
  useAvatarSessionCleanup(sessionDetails?.session.sessionId);
  const messages = useAppSelector(selectMessages);
  const isProcessing = useAppSelector(selectIsProcessing);
  const diagnosticReadiness = useAppSelector(selectDiagnosticReadiness);
  const speechState = useAppSelector(selectSpeechState);
  const avatarState = useAppSelector(selectAvatarState);
  const frameworkRubric = useAppSelector(selectframeworkCategoryStatusList);
  const scoringRubric = useAppSelector(selectScoringRubric);
  const themeMode = useAppSelector((state) => state.theme.mode);

  const speechRef = useRef<{ toggleSpeech: () => void }>(null);
  const isSmallScreen = useMediaQuery("(max-width:1000px)");
  const [activePanel, setActivePanel] = useState<SessionPanel | null>(null);
  const [isEndDiagnosticModalOpen, setIsEndDiagnosticModalOpen] = useState(false);
  const [avatarSessionStartedAt, setAvatarSessionStartedAt] = useState<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState("0:00");
  const [prescriptionIndex, setPrescriptionIndex] = useState(0);
  const [diagnosisDraft, setDiagnosisDraft] = useState("");
  const [isRubricModalOpen, setIsRubricModalOpen] = useState(false);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (avatarState.isConnected) {
      setAvatarSessionStartedAt((currentStartTime) => currentStartTime || new Date().toISOString());
      return;
    }

    setAvatarSessionStartedAt(null);
  }, [avatarState.isConnected]);

  useEffect(() => {
    if (!avatarSessionStartedAt) {
      setElapsedTime("0:00");
      return;
    }

    setElapsedTime(formatElapsedTime(avatarSessionStartedAt));

    const intervalId = window.setInterval(() => {
      setElapsedTime(formatElapsedTime(avatarSessionStartedAt));
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [avatarSessionStartedAt]);

  useEffect(() => {
    if (avatarState.isConnected) {
      dispatch(connectSpeech());
    } else {
      dispatch(disconnectSpeech());
    }
  }, [avatarState.isConnected, dispatch]);

  useEffect(() => {
    if (activePanel !== "prescriptions") {
      setPrescriptionIndex(0);
    }
  }, [activePanel]);

  useEffect(() => {
    setDiagnosisDraft("");
  }, [sessionDetails?.session.sessionId]);

  const handleEndDiagnostic = useCallback(() => {
    setIsEndDiagnosticModalOpen(true);
  }, []);

  const handleAskQuestion = useCallback(
    (question: string, fromSpeech = false, timing?: ExchangeTimingMetadata) => {
      if (diagnosticReadiness.canAskQuestions) {
        thunkDispatch(askQuestionWithSpeech({ question, fromSpeech, timing }));
      }
    },
    [diagnosticReadiness.canAskQuestions, dispatch]
  );

  const handleDiagnosisSubmit = useCallback(
    (diagnosis: string) => {
      if (diagnosticReadiness.canSubmitDiagnosis) {
        thunkDispatch(submitDiagnosis({ diagnosis }));
      }
    },
    [diagnosticReadiness.canSubmitDiagnosis, dispatch]
  );

  const handlePrimaryAction = useCallback(() => {
    if (!sessionDetails) {
      return;
    }

    if (!avatarState.isConnected) {
      thunkDispatch(connectAvatar());
      return;
    }

    dispatch(disconnectSpeech());
    thunkDispatch(disconnectAvatar());
    setAvatarSessionStartedAt(null);
    setElapsedTime("0:00");
  }, [avatarState.isConnected, sessionDetails, thunkDispatch, dispatch]);

  const togglePanel = useCallback((panel: SessionPanel) => {
    setActivePanel((currentPanel) => (currentPanel === panel ? null : panel));
  }, []);

  const sharedAvatarSection = (
    <AvatarSection
      sessionDetails={sessionDetails}
      avatarState={avatarState}
      speechState={speechState}
    />
  );

  const sharedSpeechRecognition = (
    <SpeechRecognition
      ref={speechRef}
      speechState={speechState}
      onTranscript={(transcript) => handleAskQuestion(transcript, true)}
      isProcessing={isProcessing}
      enabled={diagnosticReadiness.canUseSpeech}
    />
  );

  const prescriptionEntries = useMemo(
    () => normalizePrescriptionListForOverlay(sessionDetails?.case?.prescriptions),
    [sessionDetails?.case?.prescriptions]
  );
  const currentPrescription = prescriptionEntries[prescriptionIndex] || null;
  const currentPrescriptionPatientName =
    currentPrescription?.patient_name ||
    sessionDetails?.case?.patient_information?.full_name ||
    DISPLAY_FALLBACK;
  const currentPrescriptionDate = displayValue(currentPrescription?.written_date);
  const currentPrescriptionMedication = currentPrescription
    ? getPrescriptionMedicationLabel(currentPrescription)
    : "";

  const patientInformationRows = useMemo(
    () => [
      {
        label: "Date of Birth",
        value: formatDateOfBirthForOverlay(
          sessionDetails?.case?.patient_information?.date_of_birth,
          sessionDetails?.case?.age
        ),
      },
      { label: "Address", value: formatAddressForOverlay(sessionDetails?.case?.patient_information) },
      {
        label: "Health Card",
        value: displayValue(sessionDetails?.case?.patient_information?.health_card),
      },
    ],
    [sessionDetails]
  );

  const rubricDisplayItems = useMemo(() => {
    if (frameworkRubric.length > 0) {
      return frameworkRubric.map((item) => ({
        label: item.category_name,
        isCompleted: item.is_completed,
      }));
    }

    return scoringRubric.map((item) => ({
      label: item.scoringCategory,
      isCompleted: item.pointsScored > 0,
    }));
  }, [frameworkRubric, scoringRubric]);

  const overlayContent = useMemo(() => {
    switch (activePanel) {
      case "patient":
        return (
          <OverlayPanel
            title="Patient Information"
            icon={<PersonOutlineIcon fontSize="medium" />}
            side="left"
            onClose={() => setActivePanel(null)}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Typography sx={{ fontSize: "1.85rem", fontWeight: 600, color: "#111827", lineHeight: 1.1 }}>
                {displayValue(sessionDetails?.case?.patient_information?.full_name)}
              </Typography>
              {patientInformationRows.map((row) => (
                <Box key={row.label}>
                  <Typography sx={{ fontSize: "0.98rem", fontWeight: 600, color: "#111827", mb: 0.65 }}>
                    {row.label}
                  </Typography>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 400, color: "#374151", whiteSpace: "pre-line", lineHeight: 1.45 }}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </OverlayPanel>
        );
      case "prescriptions":
        return (
          <OverlayPanel
            title="Prescription"
            icon={<PrescriptionOverlayButtonIcon />}
            side="left"
            onClose={() => setActivePanel(null)}
            variant="prescription"
          >
            {currentPrescription ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.1 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  {[
                    ["Patient", currentPrescriptionPatientName],
                    ["Date", currentPrescriptionDate],
                  ].map(([label, value]) => (
                    <Box key={label}>
                      <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#111827", mb: 0.4 }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: "0.94rem", fontWeight: 400, color: "#1f2937", lineHeight: 1.32 }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box>
                  <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#111827", mb: 0.4 }}>
                    Medication
                  </Typography>
                  <Typography sx={{ fontSize: "0.94rem", fontWeight: 400, color: "#1f2937", lineHeight: 1.32 }}>
                    {currentPrescriptionMedication || DISPLAY_FALLBACK}
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#111827", mb: 0.4 }}>
                    Directions
                  </Typography>
                  <Typography sx={{ fontSize: "0.94rem", fontWeight: 400, color: "#1f2937", lineHeight: 1.38 }}>
                    {currentPrescription.directions || DISPLAY_FALLBACK}
                  </Typography>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  {[
                    ["Quantity", displayValue(currentPrescription.quantity)],
                    ["Refills", displayValue(currentPrescription.refills)],
                  ].map(([label, value]) => (
                    <Box key={label}>
                      <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#111827", mb: 0.4 }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: "0.94rem", fontWeight: 400, color: "#1f2937", lineHeight: 1.32 }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box>
                  <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#111827", mb: 0.4 }}>
                    Prescriber
                  </Typography>
                  <Typography sx={{ fontSize: "0.94rem", fontWeight: 400, color: "#1f2937", lineHeight: 1.32 }}>
                    {displayValue(currentPrescription.prescriber)}
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#111827", mb: 0.7 }}>
                    Signature
                  </Typography>
                  <Box
                    sx={{
                      minHeight: 42,
                      display: "flex",
                      alignItems: "flex-end",
                      pb: 0.65,
                      borderBottom: "2px solid rgba(17, 24, 39, 0.35)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: currentPrescription.signature ? "1.22rem" : "0.94rem",
                        fontStyle: currentPrescription.signature ? "italic" : "normal",
                        fontFamily: currentPrescription.signature ? '"Brush Script MT", "Segoe Script", cursive' : "inherit",
                        color: "#111827",
                      }}
                    >
                      {currentPrescription.signature || DISPLAY_FALLBACK}
                    </Typography>
                  </Box>
                </Box>

                {prescriptionEntries.length > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", pt: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.85 }}>
                      <IconButton
                        aria-label="Previous prescription"
                        onClick={() => setPrescriptionIndex((currentIndex) => (
                          currentIndex === 0 ? prescriptionEntries.length - 1 : currentIndex - 1
                        ))}
                        sx={{
                          width: 30,
                          height: 30,
                          border: "1px solid rgba(15, 23, 42, 0.1)",
                          backgroundColor: "rgba(15, 23, 42, 0.03)",
                          color: "#111827",
                          "&:hover": {
                            backgroundColor: "rgba(51, 153, 163, 0.1)",
                            borderColor: "rgba(51, 153, 163, 0.28)",
                          },
                        }}
                      >
                        <ChevronLeftIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                      {prescriptionEntries.map((entry, index) => (
                        <Box
                          key={`${entry.name}-${index}`}
                          onClick={() => setPrescriptionIndex(index)}
                          sx={{
                            width: 9,
                            height: 9,
                            borderRadius: "999px",
                            cursor: "pointer",
                            transition: "transform 180ms ease, background-color 180ms ease",
                            transform: index === prescriptionIndex ? "scale(1.05)" : "scale(1)",
                            backgroundColor: index === prescriptionIndex ? "#3399A3" : "rgba(15, 23, 42, 0.16)",
                          }}
                        />
                      ))}
                      <IconButton
                        aria-label="Next prescription"
                        onClick={() => setPrescriptionIndex((currentIndex) => (
                          currentIndex === prescriptionEntries.length - 1 ? 0 : currentIndex + 1
                        ))}
                        sx={{
                          width: 30,
                          height: 30,
                          border: "1px solid rgba(15, 23, 42, 0.1)",
                          backgroundColor: "rgba(15, 23, 42, 0.03)",
                          color: "#111827",
                          "&:hover": {
                            backgroundColor: "rgba(51, 153, 163, 0.1)",
                            borderColor: "rgba(51, 153, 163, 0.28)",
                          },
                        }}
                      >
                        <ChevronRightIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography sx={{ color: "#6b7280", fontSize: "1rem", lineHeight: 1.6 }}>
                No prescription, OTC, or NHP data has been added for this case yet.
              </Typography>
            )}
          </OverlayPanel>
        );
      case "history":
        return (
          <OverlayPanel
            title="Medical History"
            icon={<MedicalHistoryIcon />}
            side="left"
            onClose={() => setActivePanel(null)}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {([
                ["Past Medical History", sessionDetails?.case?.medical_history?.past_medical_history || []],
                ["Past Surgeries", sessionDetails?.case?.medical_history?.past_surgeries || []],
                ["Allergies", sessionDetails?.case?.medical_history?.allergies || []],
                ["Social History", sessionDetails?.case?.medical_history?.social_history || []],
              ] as Array<[string, string[]]>).map(([label, items]) => (
                <Box key={label}>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "#111827", mb: 1.1 }}>
                    {label}
                  </Typography>
                  {(items as string[]).length > 0 ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.85 }}>
                      {(items as string[]).map((item, index) => (
                        <Typography key={`${label}-${index}`} sx={{ fontSize: "1rem", fontWeight: 400, color: "#1f2937", lineHeight: 1.45 }}>
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: "1rem", color: "#6b7280" }}>
                      Not provided
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </OverlayPanel>
        );
      case "rubric":
        return (
          <OverlayPanel
            title="Rubric"
            icon={<CheckCircleOutlineIcon fontSize="small" />}
            side="right"
            onClose={() => setActivePanel(null)}
            variant="rubric"
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.4 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.05 }}>
                {rubricDisplayItems.map((item) => (
                  <Box
                    key={item.label}
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2.25 }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "999px",
                        backgroundColor: item.isCompleted ? "#666666" : "#d9d9d9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: 0.15,
                      }}
                    >
                      {item.isCompleted && <CheckIcon sx={{ fontSize: 11, color: "#ffffff" }} />}
                    </Box>
                    <Typography sx={{ fontSize: "0.86rem", fontWeight: 600, color: "#111827", lineHeight: 1.35 }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {scoringRubric.length > 0 && (
                <Typography
                  component="button"
                  type="button"
                  onClick={() => setIsRubricModalOpen(true)}
                  sx={{
                    alignSelf: "flex-start",
                    pt: 1.2,
                    px: 0,
                    border: 0,
                    background: "transparent",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: "0.95rem",
                    color: "#6b7280",
                    textAlign: "left",
                    "&:hover": {
                      color: "#3399A3",
                      textDecoration: "underline",
                    },
                    "&:focus-visible": {
                      outline: "2px solid #3399A3",
                      outlineOffset: 3,
                      borderRadius: "4px",
                    },
                  }}
                  aria-label="Open assessment rubric details"
                >
                  Score: {scoringRubric.reduce((sum, item) => sum + item.pointsScored, 0)} /{" "}
                  {scoringRubric.reduce((sum, item) => sum + item.totalPoints, 0)}
                </Typography>
              )}
            </Box>
          </OverlayPanel>
        );
      case "submit":
        return (
          <OverlayPanel
            title="Submit Diagnosis"
            icon={<DescriptionOutlinedIcon fontSize="small" />}
            side="right"
            onClose={() => setActivePanel(null)}
          >
            <DiagnosisNotes
              onSubmitDiagnosis={handleDiagnosisSubmit}
              currentCase={sessionDetails?.case || null}
              fullHeight
              diagnosisInput={diagnosisDraft}
              onDiagnosisInputChange={setDiagnosisDraft}
            />
          </OverlayPanel>
        );
      case "conversation":
        return (
          <OverlayPanel
            title="Conversation"
            icon={<ChatOutlinedIcon fontSize="small" sx={{ transform: "scaleX(-1)" }} />}
            side="right"
            onClose={() => setActivePanel(null)}
            variant="conversation"
          >
            <Box
              sx={{
                height: { xs: 500, md: 488 },
                minHeight: { xs: 500, md: 488 },
                flexShrink: 0,
              }}
            >
              <ChatContainer
                messages={messages}
                onSendMessage={handleAskQuestion}
                onSubmitDiagnosis={handleDiagnosisSubmit}
                isProcessing={isProcessing}
                currentCase={sessionDetails?.case || null}
                variant="conversationOverlay"
              />
            </Box>
          </OverlayPanel>
        );
      default:
        return null;
    }
  }, [
    activePanel,
    currentPrescriptionDate,
    currentPrescriptionMedication,
    currentPrescriptionPatientName,
    frameworkRubric,
    patientInformationRows,
    prescriptionEntries,
    prescriptionIndex,
    rubricDisplayItems,
    scoringRubric,
    sessionDetails,
    messages,
    handleAskQuestion,
    isProcessing,
    handleDiagnosisSubmit,
    diagnosisDraft,
    setIsRubricModalOpen,
  ]);

  const primaryActionLabel = useMemo(() => {
    if (avatarState.isConnecting || speechState.isConnecting) {
      return "Connecting...";
    }
    if (!avatarState.isConnected) {
      return "Speak now";
    }
    return "End conversation";
  }, [avatarState.isConnected, avatarState.isConnecting, speechState.isConnecting]);

  const isPrimaryActionConnecting = avatarState.isConnecting || speechState.isConnecting;
  const primaryActionIcon = isPrimaryActionConnecting ? (
    <CircularProgress size={20} thickness={5} color="inherit" />
  ) : (
    <MicNoneIcon />
  );

  if (isSmallScreen) {
    return (
      <>
        <GlobalStyles
          styles={{
            ".diagnosis-session-shell--dark .avatar-placeholder": {
              background: "#1f2937 !important",
              borderColor: "#334155 !important",
              color: "#cbd5e1 !important",
            },
            ".diagnosis-session-shell--dark .diagnosis-shell-button": {
              backgroundColor: "#1f2937 !important",
              borderColor: "#334155 !important",
              color: "#f8fafc !important",
            },
            ".diagnosis-session-shell--dark .diagnosis-shell-button--active": {
              backgroundColor: "rgba(51, 153, 163, 0.22) !important",
              borderColor: "#3399A3 !important",
              color: "#f8fafc !important",
            },
          }}
        />
        <Box
          className={`diagnosis-session-shell diagnosis-session-shell--${themeMode}`}
          sx={{
            minHeight: "100dvh",
            backgroundColor: "background.default",
            px: 2,
            py: 1.5,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 1.4,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "32px minmax(0, 1fr) auto",
              alignItems: "center",
              columnGap: 1,
            }}
          >
            <IconButton
              onClick={handleEndDiagnostic}
              sx={{ color: "text.primary", width: 32, height: 32, p: 0 }}
            >
              <ChevronLeftIcon sx={{ fontSize: 24 }} />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  lineHeight: 1.12,
                  fontWeight: 700,
                  color: "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {sessionDetails?.field?.field_name || "No Field Selected"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  fontWeight: 500,
                  color: "text.primary",
                  mt: 0.3,
                  lineHeight: 1.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {sessionDetails?.case?.title || "No Case Selected"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "text.primary", lineHeight: 1 }}>
                {elapsedTime}
              </Typography>
              <Button
                variant="outlined"
                onClick={handleEndDiagnostic}
                sx={{
                  height: 34,
                  px: 1.55,
                  py: 0,
                  borderRadius: "8px",
                  borderColor: "rgba(15, 23, 42, 0.18)",
                  backgroundColor: "#ffffff",
                  color: "#111827",
                  textTransform: "none",
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  lineHeight: 1,
                  minWidth: 0,
                  boxShadow: "none",
                }}
              >
                End
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              position: "relative",
              flex: "1 1 auto",
              minHeight: 0,
              display: "flex",
              alignItems: "stretch",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                minHeight: 0,
                position: "relative",
                "& .avatar-section": {
                  height: "100%",
                  minHeight: "min(56dvh, 430px) !important",
                  marginBottom: 0,
                },
                "& .avatar-placeholder": {
                  borderRadius: "16px",
                  fontSize: "1rem",
                  px: 3,
                  background: (theme) =>
                    theme.palette.mode === "dark" ? "#1f2937 !important" : "#e5e7eb !important",
                  borderColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "#334155"
                      : "rgba(15, 23, 42, 0.08)",
                  color: (theme) =>
                    theme.palette.mode === "dark" ? "#cbd5e1" : "#6b7280",
                },
                "& .avatar-video": {
                  borderRadius: "16px",
                },
              }}
            >
              {sharedAvatarSection}
              {overlayContent}
            </Box>
          </Box>

          <Box sx={{ display: "none" }}>{sharedSpeechRecognition}</Box>

          <Box
            sx={{
              minHeight: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 0.5,
            }}
          >
            {avatarState.isConnected && speechState.currentTranscript.trim() && (
              <Typography
                sx={{
                  width: "100%",
                  px: 1.6,
                  py: 0.85,
                  borderRadius: "999px",
                  backgroundColor: "rgba(15, 23, 42, 0.04)",
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  color: "#374151",
                  fontSize: "0.86rem",
                  fontWeight: 400,
                  lineHeight: 1.3,
                  textAlign: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {speechState.currentTranscript}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.2,
              pb: "max(0px, env(safe-area-inset-bottom))",
            }}
          >
            <Button
              onClick={handlePrimaryAction}
              disabled={
                !sessionDetails ||
                avatarState.isConnecting ||
                speechState.isConnecting ||
                isProcessing
              }
              startIcon={primaryActionIcon}
              aria-live="polite"
              sx={{
                alignSelf: "center",
                height: 50,
                px: 2.4,
                py: 0,
                borderRadius: "6px",
                minWidth: 168,
                backgroundColor: "#3399A3",
                color: "#ffffff",
                textTransform: "none",
                fontSize: "0.98rem",
                fontWeight: 500,
                lineHeight: 1,
                boxShadow: "none",
                "& .MuiButton-startIcon": {
                  mr: 1,
                },
                "& svg": {
                  fontSize: "20px !important",
                },
                "&:hover": {
                  backgroundColor: "#31949d",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(51, 153, 163, 0.45)",
                  color: "rgba(255,255,255,0.95)",
                },
              }}
            >
              {primaryActionLabel}
            </Button>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 0.8,
                flexWrap: "wrap",
              }}
            >
                <ShellOverlayButton
                  icon={<AssignmentIndOutlinedIcon fontSize="medium" />}
                  label="Patient Information"
                  isActive={activePanel === "patient"}
                  onClick={() => togglePanel("patient")}
                />
                <ShellOverlayButton
                  icon={<PrescriptionOverlayButtonIcon />}
                  label="Prescription"
                  isActive={activePanel === "prescriptions"}
                  onClick={() => togglePanel("prescriptions")}
                />
                <ShellOverlayButton
                  icon={<MedicalHistoryIcon />}
                  label="Medical History"
                  isActive={activePanel === "history"}
                  onClick={() => togglePanel("history")}
                />
                <ShellOverlayButton
                  icon={<CheckCircleOutlineIcon fontSize="small" />}
                  label="Rubric"
                  isActive={activePanel === "rubric"}
                  onClick={() => togglePanel("rubric")}
                />
                <ShellOverlayButton
                  icon={<DescriptionOutlinedIcon fontSize="small" />}
                  label="Submit Diagnosis"
                  isActive={activePanel === "submit"}
                  onClick={() => togglePanel("submit")}
                />
                <ShellOverlayButton
                  icon={<ChatOutlinedIcon fontSize="small" sx={{ transform: "scaleX(-1)" }} />}
                  label="Conversation"
                  isActive={activePanel === "conversation"}
                  onClick={() => togglePanel("conversation")}
                />
            </Box>
          </Box>
        </Box>
        <EndDiagnosticModal
          open={isEndDiagnosticModalOpen}
          onClose={() => setIsEndDiagnosticModalOpen(false)}
        />
        <RubricModal
          open={isRubricModalOpen}
          onOpenChange={setIsRubricModalOpen}
          frameWorkRubric={frameworkRubric}
          userRubric={scoringRubric}
        />
      </>
    );
  }

  return (
    <>
      <GlobalStyles
        styles={{
          ".diagnosis-session-shell--dark .avatar-placeholder": {
            background: "#1f2937 !important",
            borderColor: "#334155 !important",
            color: "#cbd5e1 !important",
          },
          ".diagnosis-session-shell--dark .diagnosis-shell-button": {
            backgroundColor: "#1f2937 !important",
            borderColor: "#334155 !important",
            color: "#f8fafc !important",
          },
          ".diagnosis-session-shell--dark .diagnosis-shell-button--active": {
            backgroundColor: "rgba(51, 153, 163, 0.22) !important",
            borderColor: "#3399A3 !important",
            color: "#f8fafc !important",
          },
        }}
      />
      <Box
        className={`diagnosis-session-shell diagnosis-session-shell--${themeMode}`}
        sx={{
          minHeight: "100dvh",
          backgroundColor: "background.default",
          px: { xs: 2, md: 2 },
          py: { xs: 2, md: 1.75 },
          boxSizing: "border-box",
          overflowX: "hidden",
          "@media (min-width: 1001px) and (max-height: 850px)": {
            py: 1,
          },
        }}
      >
        <Box
          sx={{
            maxWidth: 1380,
            mx: "auto",
            minHeight: "calc(100dvh - 28px)",
            display: "flex",
            flexDirection: "column",
            "@media (min-width: 1001px) and (max-height: 850px)": {
              minHeight: "calc(100dvh - 16px)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 3,
              pb: 2.75,
              flexShrink: 0,
              "@media (min-width: 1001px) and (max-height: 850px)": {
                pb: 1.5,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={handleEndDiagnostic}
                sx={{ color: "text.primary", width: 32, height: 32, p: 0 }}
              >
                <ChevronLeftIcon sx={{ fontSize: 24 }} />
              </IconButton>
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: "1.55rem", md: "1.5rem" },
                    lineHeight: 1.15,
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  {sessionDetails?.field?.field_name || "No Field Selected"}
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary", mt: 0.3, lineHeight: 1.25 }}>
                  {sessionDetails?.case?.title || "No Case Selected"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "text.primary", lineHeight: 1 }}>
                {elapsedTime}
              </Typography>
              <Button
                variant="outlined"
                onClick={handleEndDiagnostic}
                sx={{
                  height: 36,
                  px: 2,
                  py: 0,
                  borderRadius: "8px",
                  borderColor: "rgba(15, 23, 42, 0.18)",
                  backgroundColor: "#ffffff",
                  color: "#111827",
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  lineHeight: 1,
                  boxShadow: "none",
                  "&:hover": {
                    borderColor: "rgba(15, 23, 42, 0.28)",
                    backgroundColor: "rgba(15, 23, 42, 0.02)",
                  },
                }}
              >
                End Session
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              position: "relative",
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 3,
              "@media (min-width: 1001px) and (max-height: 850px)": {
                gap: 1,
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                flex: "1 1 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 600,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 1160,
                  px: { md: 7 },
                  position: "relative",
                  zIndex: 1,
                  "& .avatar-placeholder": {
                    background: (theme) =>
                      theme.palette.mode === "dark" ? "#1f2937 !important" : "#e5e7eb !important",
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "#334155"
                        : "rgba(15, 23, 42, 0.08)",
                    color: (theme) =>
                      theme.palette.mode === "dark" ? "#cbd5e1" : "#6b7280",
                  },
                }}
              >
                {sharedAvatarSection}
                {overlayContent}
              </Box>
            </Box>

            <Box sx={{ display: "none" }}>{sharedSpeechRecognition}</Box>

            <Box
              sx={{
                minHeight: 42,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                mt: -1,
                flexShrink: 0,
                "@media (min-width: 1001px) and (max-height: 850px)": {
                  minHeight: 30,
                  mt: 0,
                },
              }}
            >
              {avatarState.isConnected && speechState.currentTranscript.trim() && (
                <Typography
                  sx={{
                    maxWidth: 720,
                    px: 2.25,
                    py: 1.05,
                    borderRadius: "999px",
                    backgroundColor: "rgba(15, 23, 42, 0.04)",
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    color: "#374151",
                    fontSize: "0.95rem",
                    fontWeight: 400,
                    lineHeight: 1.35,
                    textAlign: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    "@media (min-width: 1001px) and (max-height: 850px)": {
                      py: 0.65,
                      fontSize: "0.88rem",
                      lineHeight: 1.25,
                    },
                  }}
                >
                  {speechState.currentTranscript}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                alignItems: "center",
                gap: 2,
                pb: 0.5,
                flexShrink: 0,
                "@media (min-width: 1001px) and (max-height: 850px)": {
                  gap: 1.25,
                  pb: 0,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  justifyContent: "flex-start",
                  "@media (min-width: 1001px) and (max-height: 850px)": {
                    gap: 2,
                  },
                }}
              >
                <ShellOverlayButton
                  icon={<AssignmentIndOutlinedIcon fontSize="medium" />}
                  label="Patient Information"
                  isActive={activePanel === "patient"}
                  onClick={() => togglePanel("patient")}
                />
                <ShellOverlayButton
                  icon={<PrescriptionOverlayButtonIcon />}
                  label="Prescription"
                  isActive={activePanel === "prescriptions"}
                  onClick={() => togglePanel("prescriptions")}
                />
                <ShellOverlayButton
                  icon={<MedicalHistoryIcon />}
                  label="Medical History"
                  isActive={activePanel === "history"}
                  onClick={() => togglePanel("history")}
                />
              </Box>

              <Button
                onClick={handlePrimaryAction}
                disabled={
                  !sessionDetails ||
                  avatarState.isConnecting ||
                  speechState.isConnecting ||
                  isProcessing
                }
                startIcon={primaryActionIcon}
                aria-live="polite"
                sx={{
                  height: 60,
                  px: 3,
                  py: 0,
                  borderRadius: "6px",
                  minWidth: 164,
                  backgroundColor: "#3399A3",
                  color: "#ffffff",
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 500,
                  lineHeight: 1,
                  boxShadow: "none",
                  "@media (min-width: 1001px) and (max-height: 850px)": {
                    height: 60,
                    minWidth: 164,
                    px: 2.5,
                  },
                  "& .MuiButton-startIcon": {
                    mr: 1.4,
                  },
                  "& svg": {
                    fontSize: "22px !important",
                  },
                  "&:hover": {
                    backgroundColor: "#31949d",
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "rgba(51, 153, 163, 0.45)",
                    color: "rgba(255,255,255,0.95)",
                  },
                }}
              >
                {primaryActionLabel}
              </Button>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  justifyContent: "flex-end",
                  "@media (min-width: 1001px) and (max-height: 850px)": {
                    gap: 2,
                  },
                }}
              >
                <ShellOverlayButton
                  icon={<CheckCircleOutlineIcon fontSize="small" />}
                  label="Rubric"
                  isActive={activePanel === "rubric"}
                  onClick={() => togglePanel("rubric")}
                />
                <ShellOverlayButton
                  icon={<DescriptionOutlinedIcon fontSize="small" />}
                  label="Submit Diagnosis"
                  isActive={activePanel === "submit"}
                  onClick={() => togglePanel("submit")}
                />
                <ShellOverlayButton
                  icon={<ChatOutlinedIcon fontSize="small" sx={{ transform: "scaleX(-1)" }} />}
                  label="Conversation"
                  isActive={activePanel === "conversation"}
                  onClick={() => togglePanel("conversation")}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <EndDiagnosticModal
        open={isEndDiagnosticModalOpen}
        onClose={() => setIsEndDiagnosticModalOpen(false)}
      />
      <RubricModal
        open={isRubricModalOpen}
        onOpenChange={setIsRubricModalOpen}
        frameWorkRubric={frameworkRubric}
        userRubric={scoringRubric}
      />
    </>
  );
};
